import { BackendId, QryptErrorCode } from "@libs/appliance_common";
import { http, HttpResponse } from "msw";
import { permissions, roles, users, mockSuperAdminAccessToken, externalScopeMappings, ldapConfig as initialLdapConfig, availableLdapGroups, availableOidcRoles } from "../data/userManagement";
// Mutable LDAP config state
let ldapConfig = initialLdapConfig;
// Mutable OIDC config state.
// Persisted in sessionStorage so it survives full-page navigations
// (e.g. the Keycloak login redirect round-trip).
const OIDC_STORAGE_KEY = "msw_mock_oidc_config";
function loadOidcConfig() {
    try {
        const stored = sessionStorage.getItem(OIDC_STORAGE_KEY);
        return stored ? JSON.parse(stored) : null;
    } catch  {
        return null;
    }
}
function saveOidcConfig(config) {
    if (config) {
        sessionStorage.setItem(OIDC_STORAGE_KEY, JSON.stringify(config));
    } else {
        sessionStorage.removeItem(OIDC_STORAGE_KEY);
    }
}
let oidcConfig = loadOidcConfig();
/** Expose current OIDC config so other mock handlers (e.g. nodeConf) can read it. */ export function getMockOidcConfig() {
    return oidcConfig;
}
// External scope mappings — persisted in sessionStorage so they survive
// the Keycloak redirect round-trip (full page reload).
const SCOPE_MAPPINGS_STORAGE_KEY = "msw_mock_scope_mappings";
function loadScopeMappings() {
    try {
        const stored = sessionStorage.getItem(SCOPE_MAPPINGS_STORAGE_KEY);
        if (stored) {
            const parsed = JSON.parse(stored);
            externalScopeMappings.length = 0;
            externalScopeMappings.push(...parsed);
        }
    } catch  {}
}
function saveScopeMappings() {
    sessionStorage.setItem(SCOPE_MAPPINGS_STORAGE_KEY, JSON.stringify(externalScopeMappings));
}
// Restore any persisted mappings on module init.
loadScopeMappings();
// Helper to find a user by ID
const findUserById = (id)=>users.find((u)=>u.id === id);
// Helper to find a role by ID
const findRoleById = (id)=>roles.find((r)=>r.id === Number(id));
// Helper to find a permission by ID
const findPermissionById = (id)=>permissions.find((p)=>p.id === Number(id));
export const queryHandlers = [
    http.get("/api/admin/users", ()=>{
        const res = {
            users
        };
        return HttpResponse.json(res);
    }),
    http.get("/api/admin/roles", ()=>{
        const res = {
            roles
        };
        return HttpResponse.json(res);
    }),
    http.get("/api/admin/permissions", ()=>{
        const res = {
            permissions
        };
        return HttpResponse.json(res);
    }),
    http.get("/api/admin/external-scopes", ()=>{
        const res = {
            scopes: {
                [BackendId.LDAP]: availableLdapGroups,
                [BackendId.OIDC]: availableOidcRoles
            }
        };
        return HttpResponse.json(res);
    }),
    http.get("/api/admin/external-scopes/mappings", ()=>{
        const res = {
            mappings: externalScopeMappings
        };
        return HttpResponse.json(res);
    }),
    http.get("/api/admin/auth/conf/ldap", ()=>{
        if (!ldapConfig) {
            return HttpResponse.json({
                success: false,
                message: "LDAP not configured",
                error_code: QryptErrorCode.NotFound
            }, {
                status: 404
            });
        }
        return HttpResponse.json(ldapConfig);
    }),
    http.get("/api/admin/auth/conf/oidc", ()=>{
        if (!oidcConfig) {
            return HttpResponse.json({
                success: false,
                message: "OIDC not configured",
                error_code: QryptErrorCode.NotFound
            }, {
                status: 404
            });
        }
        return HttpResponse.json(oidcConfig);
    }),
    http.get("/api/admin/auth/conf/health", ()=>{
        // Match real backend behaviour: only report health for configured backends.
        const backends = {};
        if (ldapConfig) {
            backends[BackendId.LDAP] = {
                is_healthy: true,
                error_message: null
            };
        }
        if (oidcConfig) {
            backends[BackendId.OIDC] = {
                is_healthy: true,
                error_message: null
            };
        }
        return HttpResponse.json({
            backends
        });
    })
];
export const mutationHandlers = [
    // Users
    http.post("/api/admin/users", async ({ request })=>{
        const body = await request.json();
        const newUser = {
            id: Math.random().toString(36).substr(2, 9),
            username: body.username,
            roles: [],
            permissions: [],
            is_superadmin: false,
            backend: BackendId.Local
        };
        users.push(newUser);
        const res = {
            user: newUser
        };
        return HttpResponse.json(res);
    }),
    http.put("/api/admin/users/:userId", async ({ params, request })=>{
        const { userId } = params;
        const body = await request.json();
        const user = findUserById(userId);
        if (user) {
            user.username = body.new_username;
            return HttpResponse.json({
                success: true,
                message: "User updated",
                error_code: null
            });
        }
        return HttpResponse.json({
            success: false,
            message: "User not found",
            error_code: QryptErrorCode.NotFound
        }, {
            status: 404
        });
    }),
    http.put("/api/admin/users/:userId/password", async ({ params, request })=>{
        const { userId } = params;
        const body = await request.json();
        const user = findUserById(userId);
        if (user && body.current_password && body.new_password) {
            return HttpResponse.json({
                success: true,
                message: "Password updated",
                error_code: null
            });
        }
        return HttpResponse.json({
            success: false,
            message: "Failed to update password",
            error_code: QryptErrorCode.Internal
        }, {
            status: 500
        });
    }),
    http.put("/api/admin/users/:userId/forced-password-change", async ({ params, request })=>{
        const { userId } = params;
        const body = await request.json();
        const user = findUserById(userId);
        if (user && body.new_password) {
            return HttpResponse.json({
                success: true,
                message: "Password changed",
                error_code: null
            });
        }
        return HttpResponse.json({
            success: false,
            message: "User not found",
            error_code: QryptErrorCode.NotFound
        }, {
            status: 404
        });
    }),
    http.put("/api/admin/users/:userId/reset-password", async ({ params, request })=>{
        const { userId } = params;
        const body = await request.json();
        const user = findUserById(userId);
        if (user && !user.is_superadmin && body.new_password) {
            return HttpResponse.json({
                success: true,
                message: "Password reset",
                error_code: null
            });
        }
        return HttpResponse.json({
            success: false,
            message: user?.is_superadmin ? "Cannot reset superadmin password" : "User not found",
            error_code: user?.is_superadmin ? QryptErrorCode.Unauthorized : QryptErrorCode.NotFound
        }, {
            status: user?.is_superadmin ? 403 : 404
        });
    }),
    http.delete("/api/admin/users/:userId", ({ params })=>{
        const { userId } = params;
        const index = users.findIndex((u)=>u.id === userId);
        if (index !== -1) {
            users.splice(index, 1);
            return HttpResponse.json({
                success: true,
                message: "User deleted",
                error_code: null
            });
        }
        return HttpResponse.json({
            success: false,
            message: "User not found",
            error_code: QryptErrorCode.NotFound
        }, {
            status: 404
        });
    }),
    http.post("/api/admin/users/:userId/roles", async ({ params, request })=>{
        const { userId } = params;
        const body = await request.json();
        const user = findUserById(userId);
        const role = findRoleById(body.role_id.toString());
        if (user && role) {
            if (!user.roles.some((r)=>r.id === role.id)) {
                user.roles.push(roles.find((r)=>r.id === role.id)); // Assign role without permissions
            }
            return HttpResponse.json({
                success: true,
                message: "Role assigned",
                error_code: null
            });
        }
        return HttpResponse.json({
            success: false,
            message: "User or role not found",
            error_code: QryptErrorCode.NotFound
        }, {
            status: 404
        });
    }),
    http.delete("/api/admin/users/:userId/roles/:roleId", ({ params })=>{
        const { userId, roleId } = params;
        const user = findUserById(userId);
        if (user) {
            user.roles = user.roles.filter((r)=>r.id.toString() !== roleId);
            return HttpResponse.json({
                success: true,
                message: "Role removed",
                error_code: null
            });
        }
        return HttpResponse.json({
            success: false,
            message: "User not found",
            error_code: QryptErrorCode.NotFound
        }, {
            status: 404
        });
    }),
    // Roles
    http.post("/api/admin/roles", async ({ request })=>{
        const body = await request.json();
        const newRole = {
            id: Date.now(),
            name: body.name,
            description: body.description,
            permissions: [],
            is_superadmin: false
        };
        roles.push(newRole);
        const res = {
            role: newRole
        };
        return HttpResponse.json(res);
    }),
    http.put("/api/admin/roles/:roleId", async ({ params, request })=>{
        const { roleId } = params;
        const body = await request.json();
        const role = findRoleById(roleId);
        if (role) {
            role.name = body.new_name;
            role.description = body.new_description;
            return HttpResponse.json({
                success: true,
                message: "Role updated",
                error_code: null
            });
        }
        return HttpResponse.json({
            success: false,
            message: "Role not found",
            error_code: QryptErrorCode.NotFound
        }, {
            status: 404
        });
    }),
    http.delete("/api/admin/roles/:roleId", ({ params })=>{
        const { roleId } = params;
        const index = roles.findIndex((r)=>r.id.toString() === roleId);
        if (index !== -1) {
            roles.splice(index, 1);
            // Also remove this role from all users
            users.forEach((user)=>{
                user.roles = user.roles.filter((r)=>r.id.toString() !== roleId);
            });
            return HttpResponse.json({
                success: true,
                message: "Role deleted",
                error_code: null
            });
        }
        return HttpResponse.json({
            success: false,
            message: "Role not found",
            error_code: QryptErrorCode.NotFound
        }, {
            status: 404
        });
    }),
    http.post("/api/admin/roles/:roleId/permissions", async ({ params, request })=>{
        const { roleId } = params;
        const body = await request.json();
        const role = findRoleById(roleId);
        const permission = findPermissionById(body.permission_id.toString());
        if (role && permission) {
            if (!role.permissions.some((p)=>p.id === permission.id)) {
                role.permissions.push(permission);
            }
            return HttpResponse.json({
                success: true,
                message: "Permission assigned",
                error_code: null
            });
        }
        return HttpResponse.json({
            success: false,
            message: "Role or permission not found",
            error_code: QryptErrorCode.NotFound
        }, {
            status: 404
        });
    }),
    http.delete("/api/admin/roles/:roleId/permissions/:permissionId", ({ params })=>{
        const { roleId, permissionId } = params;
        const role = findRoleById(roleId);
        if (role) {
            role.permissions = role.permissions.filter((p)=>p.id.toString() !== permissionId);
            return HttpResponse.json({
                success: true,
                message: "Permission removed",
                error_code: null
            });
        }
        return HttpResponse.json({
            success: false,
            message: "Role not found",
            error_code: QryptErrorCode.NotFound
        }, {
            status: 404
        });
    }),
    // External Group-Role Mapping endpoints
    http.post("/api/admin/external-scopes/:externalScopeId/roles", async ({ params, request })=>{
        const { externalScopeId } = params;
        const body = await request.json();
        const decodedGroupId = decodeURIComponent(externalScopeId);
        // Check if role exists
        const role = findRoleById(body.role_id.toString());
        if (!role) {
            return HttpResponse.json({
                success: false,
                message: "Role not found",
                error_code: QryptErrorCode.NotFound
            }, {
                status: 404
            });
        }
        // Remove existing mapping for this group (if any)
        const existingIndex = externalScopeMappings.findIndex((m)=>m.external_scope_id === decodedGroupId);
        if (existingIndex !== -1) {
            externalScopeMappings.splice(existingIndex, 1);
        }
        // Add new mapping with proper types
        const newMapping = {
            id: externalScopeMappings.length + 1,
            external_scope_id: decodedGroupId,
            role_id: body.role_id,
            created_at: new Date().toISOString()
        };
        externalScopeMappings.push(newMapping);
        saveScopeMappings();
        return HttpResponse.json({
            success: true,
            message: "Role assigned to external group successfully",
            error_code: null
        });
    }),
    http.delete("/api/admin/external-scopes/:externalScopeId/roles/:roleId", ({ params })=>{
        const { externalScopeId } = params;
        const decodedGroupId = decodeURIComponent(externalScopeId);
        // Remove mapping for this group
        const index = externalScopeMappings.findIndex((m)=>m.external_scope_id === decodedGroupId);
        if (index !== -1) {
            externalScopeMappings.splice(index, 1);
            saveScopeMappings();
        }
        // Return success even if mapping didn't exist (idempotent)
        return HttpResponse.json({
            success: true,
            message: "Role removed from external group successfully",
            error_code: null
        });
    }),
    // LDAP config endpoints
    http.post("/api/admin/auth/conf/ldap", async ({ request })=>{
        const body = await request.json();
        // Update the LDAP config with the new values
        ldapConfig = {
            ...body,
            created_at: ldapConfig?.created_at || new Date().toISOString()
        };
        return HttpResponse.json({
            success: true,
            message: "LDAP config successfully updated",
            error_code: null
        });
    }),
    http.delete("/api/admin/auth/conf/ldap", ()=>{
        // Clear the LDAP config
        ldapConfig = null;
        return HttpResponse.json({
            success: true,
            message: "LDAP config successfully deleted",
            error_code: null
        });
    }),
    // OIDC config endpoints
    http.post("/api/admin/auth/conf/oidc", async ({ request })=>{
        const body = await request.json();
        oidcConfig = {
            server_url: body.server_url,
            client_id: body.client_id,
            realm: body.realm,
            has_client_secret: !!(body.client_secret || oidcConfig?.has_client_secret),
            auth_client_id: body.auth_client_id
        };
        saveOidcConfig(oidcConfig);
        return HttpResponse.json({
            success: true,
            message: "OIDC config successfully updated",
            error_code: null
        });
    }),
    http.delete("/api/admin/auth/conf/oidc", ()=>{
        oidcConfig = null;
        saveOidcConfig(null);
        return HttpResponse.json({
            success: true,
            message: "OIDC config successfully deleted",
            error_code: null
        });
    })
];
export const createTokenHandler = (accessToken)=>{
    return http.post("/api/admin/token", async ({ request })=>{
        const body = await request.json();
        if (body && body.username && body.password) {
            const res = {
                access_token: accessToken,
                token_type: "Bearer",
                expires_in: 120,
                refresh_token: "mock-refresh-token"
            };
            return HttpResponse.json(res);
        } else {
            const res = {
                success: false,
                message: "Bad request",
                error_code: QryptErrorCode.SerdeError
            };
            return HttpResponse.json(res, {
                status: 401
            });
        }
    });
};
export const authHandlers = [
    createTokenHandler(mockSuperAdminAccessToken),
    http.post("/api/admin/refresh", async ({ request })=>{
        const body = await request.json();
        if (body && body.refresh_token) {
            const res = {
                access_token: mockSuperAdminAccessToken,
                expires_in: 120,
                token_type: "Bearer",
                refresh_token: "new-refresh-token"
            };
            return HttpResponse.json(res);
        } else {
            const res = {
                success: false,
                message: "Invalid refresh token",
                error_code: QryptErrorCode.Forbidden
            };
            return HttpResponse.json(res, {
                status: 401
            });
        }
    })
];
const handlers = [
    ...queryHandlers,
    ...mutationHandlers,
    ...authHandlers
];
export default handlers;
